import { Signal } from "@suse-o11y/signal";
import type { SignalForm } from "@suse-o11y/signal/form";
import { usePermanent } from "@suse-o11y/use-permanent";
import { useRequiredContext } from "@suse-o11y/use-required-context";
import { nanoid } from "nanoid";
import { createContext, useMemo } from "react";

export interface FormContextValue {
	/**
	 * The form context distributes the form id so the form fields can reference it as form={FormContextValue.id} attribute.
	 */
	id: string;
}

export const FormContext = createContext<FormContextValue | undefined>(undefined);

export const FormContextProvider = FormContext.Provider;

export interface FormFieldContextValue {
	/**
	 * The id of the form control element so FormLabel can reference it as htmlFor={FormFieldContextValue.id} attribute.
	 */
	id: string;

	/**
	 * State of the form. It is used to
	 * - determine the dirty/submitting states
	 * - extract validation errors, values
	 * - reset the dirty values
	 * - and do ANYTHING related to the form state
	 */
	state: SignalForm;

	/**
	 * Keeps track of the number of FormMessage.variant=error descendants.
	 * When it is 0, a fallback error message is displayed.
	 */
	errorMessageDescendants: Signal<number>;
}

export const FormFieldContext = createContext<undefined | FormFieldContextValue>(undefined);

export const FormFieldContextProvider = FormFieldContext.Provider;

export function useRequiredFormFieldContext(): FormFieldContextValue {
	return useRequiredContext(FormFieldContext, {
		providerName: "FormField",
	});
}

export function useInitFormFieldContext(state: SignalForm): FormFieldContextValue {
	const partialContext = usePermanent(() => ({
		id: nanoid(),
		errorMessageDescendants: Signal(0),
	}));

	return useMemo(() => ({ ...partialContext, state }), [partialContext, state]);
}
